import { useState } from 'react';
import {
    AppBar,
    Toolbar,
    Box,
    IconButton,
    useTheme,
    Typography,
    Menu,
    MenuItem,
    Avatar,
    Divider,
    ListItemIcon,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { 
    Brightness4, 
    Brightness7,
    Logout
} from '@mui/icons-material';
import { useAuth } from '../contexts/auth-context';
import { useThemeMode } from '../contexts/ThemeContext';
import { getCustomerLogoUrl } from '../lib/customer-logos';
import logo from '../assets/icon.png';

const Header = () => {
    const { user, signOut, isAdmin, pendingPasswordReset, brandingEnabled, customLogo } = useAuth();
    const showUserMenu = user && !pendingPasswordReset;
    const showBranding = brandingEnabled !== false;
    const customLogoUrl = showBranding ? getCustomerLogoUrl(customLogo) : null;
    const { mode, toggleTheme } = useThemeMode();
    const theme = useTheme();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleSignOut = async () => {
        handleMenuClose();
        await signOut();
        navigate('/auth');
    };

    // Get user initials for avatar
    const getUserInitials = () => {
        if (!user?.email) return '?';
        const email = user.email;
        const parts = email.split('@')[0].split('.');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return email[0].toUpperCase();
    };

    return (
        <AppBar 
            position="fixed"
            sx={{
                backgroundColor: mode === 'light' 
                    ? theme.palette.background.default
                    : theme.palette.background.paper,
                borderBottom: `1px solid ${theme.palette.dividerSecondary}`,
                zIndex: theme.zIndex.drawer + 1,
            }}
            elevation={0}
        >
            <Toolbar sx={{ py: 1, minHeight: '64px', pl: 0, pr: 0 }}>
                {/* Logo fixed in top left corner */}
                {showBranding && (
                    <Box
                        component={Link}
                        to="/"
                        sx={{
                            position: 'absolute',
                            left: '0.8rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textDecoration: 'none',
                            zIndex: theme.zIndex.drawer + 2,
                            gap: 1.25,
                        }}
                    >
                        <img 
                            src={logo} 
                            alt="Aquatic Labs Logo" 
                            style={{ 
                                height: '40px', 
                                width: '40px',
                                objectFit: 'contain',
                                filter: mode === 'light' ? 'brightness(0)' : 'brightness(0) invert(1)',
                                display: 'block'
                            }}
                        />
                        {customLogoUrl && (
                            <>
                                <Typography
                                    component="span"
                                    aria-hidden="true"
                                    sx={{
                                        fontSize: '1.5rem',
                                        fontWeight: 300,
                                        lineHeight: 1,
                                        color: theme.palette.text.primary,
                                        userSelect: 'none',
                                    }}
                                >
                                    +
                                </Typography>
                                <img
                                    src={customLogoUrl}
                                    alt="Customer Logo"
                                    style={{
                                        height: '32px',
                                        width: 'auto',
                                        maxWidth: '180px',
                                        objectFit: 'contain',
                                        filter: mode === 'light' ? 'brightness(0)' : 'brightness(0) invert(1)',
                                        display: 'block',
                                    }}
                                />
                            </>
                        )}
                    </Box>
                )}

                {/* Spacer */}
                <Box sx={{ flexGrow: 1 }} />

                {/* Theme Toggle and User Menu on the right */}
                <Box sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                }}>
                    <IconButton 
                        onClick={toggleTheme} 
                        color="inherit"
                        sx={{
                            color: theme.palette.text.primary,
                            '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                            }
                        }}
                        aria-label="toggle theme"
                    >
                        {mode === 'light' ? <Brightness4 /> : <Brightness7 />}
                    </IconButton>
                    {showUserMenu && (
                        <>
                            <IconButton
                                onClick={handleMenuOpen}
                                size="small"
                                sx={{
                                    ml: 1,
                                    '&:hover': {
                                        backgroundColor: theme.palette.action.hover,
                                    }
                                }}
                                aria-controls={menuOpen ? 'account-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={menuOpen ? 'true' : undefined}
                            >
                                <Avatar 
                                    sx={{ 
                                        width: 36, 
                                        height: 36,
                                        bgcolor: theme.palette.primary.main,
                                        color: theme.palette.primary.contrastText,
                                        fontSize: '0.875rem',
                                        fontWeight: 600
                                    }}
                                >
                                    {getUserInitials()}
                                </Avatar>
                            </IconButton>
                            <Menu
                                anchorEl={anchorEl}
                                id="account-menu"
                                open={menuOpen}
                                onClose={handleMenuClose}
                                onClick={handleMenuClose}
                                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                PaperProps={{
                                    elevation: 0,
                                    sx: {
                                        overflow: 'visible',
                                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                        mt: 1.5,
                                        minWidth: 220,
                                        '& .MuiAvatar-root': {
                                            width: 32,
                                            height: 32,
                                            ml: -0.5,
                                            mr: 1,
                                        },
                                        '&:before': {
                                            content: '""',
                                            display: 'block',
                                            position: 'absolute',
                                            top: 0,
                                            right: 14,
                                            width: 10,
                                            height: 10,
                                            bgcolor: 'background.paper',
                                            transform: 'translateY(-50%) rotate(45deg)',
                                            zIndex: 0,
                                        },
                                    },
                                }}
                            >
                                <Box sx={{ px: 2, py: 1.5 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                        {user.email}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {isAdmin ? 'Administrator' : 'Customer'}
                                    </Typography>
                                </Box>
                                <Divider />
                                <MenuItem onClick={handleSignOut}>
                                    <ListItemIcon>
                                        <Logout fontSize="small" />
                                    </ListItemIcon>
                                    Sign Out
                                </MenuItem>
                            </Menu>
                        </>
                    )}
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default Header;
